// Helpers for the custom participant restriction rules (application and website blacklist).
// Custom rules are kept even if they overlap a preset, so a preset match is only a hint.

export function normalizeRuleValue(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/+$/, "");
}

// Website presets store their url as a regex, e.g. ^(?:https?:\/\/)?(?:.*\.)?comdirect\.de
export function presetUrlToDomain(url) {
  return String(url || "")
    .replace(/^\^/, "")
    .replace("(?:https?:\\/\\/)?", "")
    .replace("(?:.*\\.)?", "")
    .replace(/\\(.)/g, "$1");
}

export function appDisplayName(appname) {
  const base = String(appname || "")
    .trim()
    .replace(/\.exe$/i, "");
  return base.charAt(0).toUpperCase() + base.slice(1);
}

export function websiteDisplayName(url) {
  return normalizeRuleValue(url).split("/")[0];
}

export function appMatches(rule, value) {
  return (
    !!normalizeRuleValue(value) &&
    normalizeRuleValue(rule.appname) === normalizeRuleValue(value)
  );
}

export function websiteMatches(rule, value) {
  const normalizedValue = normalizeRuleValue(value);
  if (!normalizedValue) return false;
  if (normalizeRuleValue(rule.url) === normalizedValue) return true;
  try {
    return new RegExp(rule.url, "i").test(normalizedValue);
  } catch {
    return false;
  }
}

export function findPresetMatch(groups, value, matches) {
  for (const group of groups || []) {
    const preset = (group.preset || []).find((rule) => matches(rule, value));
    if (preset) return { group, preset };
  }
  return null;
}

export function findCustomMatch(groups, value, matches, skip = {}) {
  return (groups || []).some((group, listId) =>
    (group.user || []).some(
      (rule, ruleId) =>
        !(listId === skip.listId && ruleId === skip.ruleId) &&
        matches(rule, value),
    ),
  );
}

// Preset suggestions for the rule input, matched by preset name, value or list name.
export function findPresetSuggestions(groups, term, toValue, limit = 6) {
  const normalizedTerm = normalizeRuleValue(term);
  if (normalizedTerm.length < 2) return [];

  const suggestions = [];
  for (const group of groups || []) {
    for (const preset of group.preset || []) {
      const value = toValue(preset);
      if (normalizeRuleValue(value) === normalizedTerm) return [];
      if (
        [preset.name, value, group.name].some((text) =>
          normalizeRuleValue(text).includes(normalizedTerm),
        )
      )
        suggestions.push({ group: group.name, preset, value });
    }
  }
  return suggestions.slice(0, limit);
}
